//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { disciplineAPI } from "./disciplinesAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type DisciplineDetails,
  type CreateDisciplineInput,
  type UpdateDisciplineInput,
} from "./types/types";


export const useDisciplines = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "disciplines",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      disciplineAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const discipline = data?.disciplines || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createDisciplineMutation = useMutation({
    mutationFn: (disciplineData: CreateDisciplineInput) =>
      disciplineAPI.create(disciplineData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["disciplines"] });
    },
  });

  const updateDisciplineMutation = useMutation({
    mutationFn: ({ id, disciplineData }: UpdateDisciplineInput) =>
      disciplineAPI.update(id, disciplineData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["disciplines"] });
    },
  });

  const deleteDisciplineMutation = useMutation({
    mutationFn: (id: string) => disciplineAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["disciplines"] });
    },
  });

  const getDisciplineById = useCallback(
    async (id: string): Promise<DisciplineDetails | null> => {
      try {
        const disciplineDetails = await disciplineAPI.getById(id, token);
        return disciplineDetails;
      } catch (err) {
        console.error("Error fetching discipline by ID:", err);
        return null;
      }
    },
    [token]
  );


  return {
    discipline,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createDisciplines: createDisciplineMutation.mutate,
    updateDisciplines: updateDisciplineMutation.mutate,
    deleteDisciplines: deleteDisciplineMutation.mutate,
    getDisciplineById,
    changePage,
    isCreating: createDisciplineMutation.isPending,
    isUpdating: updateDisciplineMutation.isPending,
    isDeleting: deleteDisciplineMutation.isPending,
  };
};
